import React, { useState } from "react";
import { Home, Eye, EyeOff, Lock, CalendarCheck, RefreshCcw, BarChart3, ArrowRight } from "lucide-react";
import { useSettings } from "../context/SettingsContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/* The admin sign-in. The brand panel is painted in the tenant's OWN theme
   colours (--color-primary / --color-accent), so every property's login looks
   like theirs, whichever of the colour themes they picked. */
export default function LoginPage({ onLogin }) {
  const { hotelName, logoUrl } = useSettings();
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [showPw,   setShowPw]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res  = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Invalid credentials");
      onLogin(json.token, json.features);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const Logo = ({ size }) => logoUrl
    ? <img src={logoUrl} alt="" className="rounded-2xl object-cover bg-white shadow-lg" style={{ width: size, height: size }} />
    : (
      <div className="rounded-2xl bg-white/20 ring-1 ring-white/40 backdrop-blur flex items-center justify-center shadow-lg" style={{ width: size, height: size }}>
        <Home size={size * 0.46} className="text-white" />
      </div>
    );

  return (
    <div className="min-h-screen bg-white flex flex-col lg:flex-row">
      {/* ── Brand panel, in the tenant's theme ── */}
      <aside className="relative overflow-hidden lg:w-[50%] px-6 pt-10 pb-16 lg:p-14 flex flex-col justify-between text-white"
        style={{ background: "linear-gradient(135deg, var(--color-primary), var(--color-accent))" }}>
        <div className="tl-orbs" aria-hidden="true">
          <span className="tl-orb" style={{ top: "-15%", right: "-10%" }} />
          <span className="tl-orb" style={{ bottom: "-25%", left: "-15%", width: "70%", height: "70%", animationDelay: "-7s" }} />
        </div>
        <div className="tl-dots" aria-hidden="true" />

        <div className="relative flex items-center gap-3">
          <Logo size={44} />
          <div className="leading-tight min-w-0">
            <p className="font-semibold tracking-tight truncate">{hotelName}</p>
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/70">Property admin</p>
          </div>
        </div>

        <div className="relative mt-10 lg:mt-0 max-w-md">
          <h1 className="text-[30px] lg:text-[42px] leading-[1.08] font-semibold tracking-[-0.025em]">
            Run your property,<br />beautifully.
          </h1>
          <p className="hidden sm:block mt-4 text-[14px] leading-relaxed text-white/80">
            Bookings, rates and every channel — kept in sync, all in one place.
          </p>
          <div className="hidden lg:grid grid-cols-3 gap-3 mt-10">
            {[
              { icon: CalendarCheck, label: "Bookings" },
              { icon: RefreshCcw,    label: "Channel sync" },
              { icon: BarChart3,     label: "Reports" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="rounded-2xl bg-white/10 ring-1 ring-white/25 backdrop-blur-md px-4 py-4">
                <Icon size={18} className="text-white" />
                <p className="text-[12px] font-medium text-white/90 mt-3">{label}</p>
              </div>
            ))}
          </div>
        </div>

        <p className="relative hidden lg:block text-[11px] text-white/60">Powered by HostSync</p>
      </aside>

      {/* ── Form ── */}
      <main className="relative flex-1 flex items-start lg:items-center justify-center px-5 pb-12 lg:p-10 -mt-8 lg:mt-0">
        <div className="w-full max-w-[400px] bg-white rounded-3xl shadow-[0_24px_60px_-20px_rgba(15,23,42,0.25)] ring-1 ring-gray-100 p-7 sm:p-9 lg:shadow-none lg:ring-0">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-white shadow-lg mb-5"
            style={{ background: "linear-gradient(135deg, var(--color-primary), var(--color-accent))" }}>
            <Lock size={18} />
          </div>
          <h2 className="text-[24px] font-semibold tracking-tight text-gray-900">Welcome back</h2>
          <p className="text-[13.5px] text-gray-500 mt-1.5 mb-7">Sign in to manage {hotelName}.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label htmlFor="login-email" className="block text-[12.5px] font-medium text-gray-600 mb-1.5">Email</label>
              <input id="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com" autoComplete="username" required
                className="w-full h-12 rounded-xl border border-gray-200 bg-gray-50/60 px-4 text-[14px] text-gray-900 placeholder-gray-400
                  focus:outline-none focus:bg-white focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent transition-all" />
            </div>

            <div>
              <label htmlFor="login-password" className="block text-[12.5px] font-medium text-gray-600 mb-1.5">Password</label>
              <div className="relative">
                <input id="login-password" type={showPw ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••" autoComplete="current-password" required
                  className="w-full h-12 rounded-xl border border-gray-200 bg-gray-50/60 px-4 pr-12 text-[14px] text-gray-900 placeholder-gray-400
                    focus:outline-none focus:bg-white focus:ring-2 focus:ring-[var(--color-primary)] focus:border-transparent transition-all" />
                <button type="button" onClick={() => setShowPw(v => !v)} aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-1 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors">
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3">
                <p className="text-[13px] text-red-600">{error}</p>
              </div>
            )}

            <button type="submit" disabled={loading}
              className="group w-full h-12 rounded-xl text-white font-semibold text-[14px] shadow-lg
                flex items-center justify-center gap-2 hover:brightness-105 hover:-translate-y-px active:translate-y-0
                disabled:opacity-60 transition-all mt-2"
              style={{ background: "linear-gradient(135deg, var(--color-primary), var(--color-accent))" }}>
              {loading ? "Signing in…" : <>Sign in <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" /></>}
            </button>
          </form>

          <p className="mt-7 flex items-center justify-center gap-1.5 text-[11.5px] text-gray-400">
            <Lock size={11} /> Secure admin access · {hotelName}
          </p>
        </div>
      </main>
    </div>
  );
}
